import { useState } from "react";

import { C, FEEDBACK, IG_HANDLE, IMG_LOGO, SHIPPING_ZONES } from "../data/handcraftsConfig";

function detectDeliveryZone(rawLocation) {
  const value = rawLocation.trim().toLowerCase();
  if (!value) return null;

  const metroKeywords = ["san salvador", "santa tecla", "antiguo cuscatlan", "soyapango", "mejicanos", "ilopango"];
  const localKeywords = ["santa ana", "san miguel", "sonsonate", "ahuachapan", "la libertad", "chalatenango", "cuscatlan", "la paz", "la union", "morazan", "san vicente", "cabanas", "usulutan"];
  const usKeywords = ["usa", "united states", "estados unidos", "texas", "california", "maryland", "virginia", "new york", "florida"];
  const pickupKeywords = ["pickup", "recoger", "retiro", "en persona"];

  if (pickupKeywords.some((keyword) => value.includes(keyword))) return "pickup";
  if (metroKeywords.some((keyword) => value.includes(keyword))) return "sv_metro";
  if (localKeywords.some((keyword) => value.includes(keyword))) return "sv_local";
  if (usKeywords.some((keyword) => value.includes(keyword))) return "us";
  if (value.includes("el salvador")) return "sv_local";

  return null;
}

function PaymentResultPage({ orderId, paymentStatus, t }) {
  const isSpanish = typeof t.nav_inicio === "string" && t.nav_inicio.toLowerCase() === "inicio";
  const stateCopy = paymentStatus === "paid"
    ? {
        state: isSpanish ? "Pagado" : "Paid",
        title: isSpanish ? "Pago confirmado" : "Payment confirmed",
        description: isSpanish ? "Wompi confirmó tu pago. Abu ya tiene tu pedido en cola." : "Wompi confirmed your payment. Abu has your order in the queue.",
        accent: FEEDBACK.success
      }
    : paymentStatus === "pending_review"
      ? {
          state: isSpanish ? "En revisión" : "Under review",
          title: isSpanish ? "Pago en revisión" : "Payment under review",
          description: isSpanish ? "Recibimos tu pago y esperamos la confirmación de Wompi. No hace falta que pagués otra vez." : "We received your payment and are waiting on Wompi to confirm. No need to pay again.",
          accent: FEEDBACK.warning
        }
      : {
          state: isSpanish ? "Verificando" : "Verifying",
          title: isSpanish ? "Estamos verificando tu pago" : "We are verifying your payment",
          description: isSpanish ? "Tu pedido quedó registrado. Esta página se actualiza sola cuando Wompi responde." : "Your order is registered. This page updates itself once Wompi responds.",
          accent: C.inkStrong
        };

  return (
    <div style={{maxWidth:900,margin:"0 auto",padding:"var(--space-section) var(--gutter)"}}>
      <div style={{background:C.surface,borderRadius:2,padding:"var(--space-group) var(--gutter)",border:`1.5px solid ${C.rule}`,textAlign:"center"}}>
        <img src={IMG_LOGO} alt="Handcrafts By Abu" style={{width:64,height:64,objectFit:"contain",marginBottom:"var(--space-block)"}}/>
        <div className="status-label" style={{color:stateCopy.accent,marginBottom:"var(--space-block)"}}>{stateCopy.state}</div>
        <h1 className="serif" style={{marginBottom:10,color:C.ink}}>{stateCopy.title}</h1>
        <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.8,maxWidth:620,margin:"0 auto 24px"}}>{stateCopy.description}</p>
        <div style={{background:C.surface,borderRadius:2,padding:"18px 20px",display:"inline-block",minWidth:260}}>
          <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginBottom:6,letterSpacing:"1px",textTransform:"uppercase"}}>
            {isSpanish ? "Número de pedido" : "Order number"}
          </div>
          <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{orderId || "—"}</div>
        </div>
        <div style={{display:"flex",justifyContent:"center",gap:12,flexWrap:"wrap",marginTop:24}}>
          <a href={`https://www.instagram.com/${IG_HANDLE}/`} target="_blank" rel="noreferrer" className="btn btn-purple sans" style={{textDecoration:"none",padding:"13px 24px"}}>
            Instagram @{IG_HANDLE}
          </a>
          <a href="?page=tienda" className="btn btn-outline sans" style={{textDecoration:"none",padding:"13px 24px"}}>
            {isSpanish ? "Seguir comprando" : "Keep shopping"}
          </a>
        </div>
      </div>
    </div>
  );
}

function SiteFooter({ t }) {
  return (
    <footer className="on-ink" style={{background:C.ink,padding:"var(--space-section) var(--gutter) var(--space-group)",textAlign:"center"}}>
      <span className="logo-on-ink" style={{width:56,height:56,margin:"0 auto var(--space-block)"}} role="img" aria-label="Handcrafts By Abu"/>
      <p className="sans" style={{fontSize:"var(--text-body)",marginBottom:8}}>{t.footer_sub}</p>
      <a href={`https://www.instagram.com/${IG_HANDLE}/`} target="_blank" rel="noreferrer" className="sans" style={{fontSize:"var(--text-body)",textDecoration:"none"}}>@{IG_HANDLE}</a>
      <p className="sans" style={{fontSize:"var(--text-label)",marginTop:20}}>{t.footer_copy}</p>
    </footer>
  );
}

function NavBar({
  count,
  goto,
  handleLogoClick,
  lang,
  menuOpen,
  onCartOpen,
  onCustomOpen,
  onToggleLanguage,
  page,
  setMenuOpen,
  t
}) {
  return (
    <>
      <header style={{background:C.surface,borderBottom:`1.5px solid ${C.rule}`,position:"sticky",top:0,zIndex:50}}>
        <div className="nav-inner" style={{maxWidth:1100,margin:"0 auto",padding:"0 var(--gutter)",height:68,display:"flex",alignItems:"center",gap:16}}>
          <div className="nav-logo-wrap" style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",flexShrink:0}} onClick={handleLogoClick}>
            <img src={IMG_LOGO} alt="Handcrafts By Abu" style={{height:42,objectFit:"contain"}}/>
            <span style={{color:C.ink,letterSpacing:"-0.5px",lineHeight:1.1,userSelect:"none"}}>
              Handcrafts<br/><span style={{color:C.inkStrong}}>by Abu</span>
            </span>
          </div>
          <nav className="desktop-nav" style={{display:"flex",gap:32,alignItems:"center",flex:1,justifyContent:"center"}}>
            {[["inicio",t.nav_inicio],["tienda",t.nav_tienda],["entrega",t.nav_entrega],["nosotros",t.nav_nosotros]].map(([p,l])=>(
              <button key={p} className={`nav-link${page===p?" active":""}`} onClick={()=>goto(p)}>{l}</button>
            ))}
          </nav>
          <div className="desktop-actions" style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
            <button onClick={onToggleLanguage}
              style={{background:"none",border:`1.5px solid ${C.rule}`,borderRadius:2,padding:"5px 12px",cursor:"pointer",fontSize:"var(--text-label)",color:C.ink,display:"flex",alignItems:"center",gap:5}}>
              {lang==="es"?"US EN":"SV ES"}
            </button>
            <button className="btn btn-outline" style={{fontSize:"var(--text-body)",padding:"9px 16px"}} onClick={onCustomOpen}>{t.nav_encargo}</button>
            <button className="btn btn-solid" style={{position:"relative",padding:"9px 18px",fontSize:"var(--text-body)"}} onClick={onCartOpen}>
              {t.nav_carrito}
              {count>0&&<span style={{position:"absolute",top:-9,right:-9,background:C.inkStrong,color:C.background,width:22,height:22,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"var(--text-label)"}}>{count}</span>}
            </button>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",marginLeft:"auto"}} className="mobile-actions">
            <button onClick={onToggleLanguage}
              style={{background:"none",border:`1.5px solid ${C.rule}`,borderRadius:2,padding:"4px 9px",cursor:"pointer",fontSize:"var(--text-label)",color:C.ink}}>
              {lang==="es"?"US":"SV"}
            </button>
            <button className="btn btn-solid" style={{position:"relative",padding:"9px 14px",fontSize:"var(--text-body)"}} onClick={onCartOpen}>
              {t.nav_carrito}{count>0&&<span style={{position:"absolute",top:-8,right:-8,background:C.inkStrong,color:C.background,width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"var(--text-label)"}}>{count}</span>}
            </button>
            <button className={`hamburger${menuOpen?" open":""}`} onClick={()=>setMenuOpen(m=>!m)} aria-label="Menu">
              <span/><span/><span/>
            </button>
          </div>
        </div>
      </header>
      <div className={`mobile-menu${menuOpen?" open":""}`}>
        <img src={IMG_LOGO} alt="Handcrafts By Abu" style={{height:48,width:48,objectFit:"contain",marginBottom:"var(--space-block)"}}/>
        {[["inicio",t.nav_inicio_m],["tienda",t.nav_tienda_m],["entrega",t.nav_entrega_m],["nosotros",t.nav_nosotros_m]].map(([p,l])=>(
          <button key={p} className={`mobile-nav-link${page===p?" active":""}`} onClick={()=>goto(p)}>{l}</button>
        ))}
        <div style={{marginTop:24,display:"flex",flexDirection:"column",gap:12}}>
          <button className="btn btn-solid btn-full" style={{fontSize:"var(--text-body)",padding:"15px 0"}} onClick={()=>{setMenuOpen(false);onCartOpen();}}>{t.nav_carrito}{count>0&&` (${count})`}</button>
          <button className="btn btn-outline btn-full" style={{fontSize:"var(--text-body)",padding:"13px 0"}} onClick={()=>{setMenuOpen(false);onCustomOpen();}}>{t.nav_encargo_m}</button>
          <a href={`https://www.instagram.com/${IG_HANDLE}/`} target="_blank" rel="noreferrer" className="btn btn-purple btn-full sans" style={{fontSize:"var(--text-body)",padding:"13px 0",textDecoration:"none",marginTop:4}}>Instagram @{IG_HANDLE}</a>
        </div>
      </div>
    </>
  );
}

function HomePage({
 ProductCard,
  addedId,
  addToCart,
  allProducts,
  goto,
  recentProducts,
  setCheckoutOpen,
  setCustomOpen,
  setDetailItem,
  soldIds,
  t
}) {
  return (
    <div>
      <section className="hero-section" style={{background:C.background,paddingTop:"var(--space-section)",paddingBottom:0,position:"relative",overflow:"hidden"}}>
        <div style={{padding:"0 var(--gutter) var(--space-section)",maxWidth:1100,margin:"0 auto"}}>
          <div style={{maxWidth:600}}>
            <div className="sans" style={{fontSize:"var(--text-label)",letterSpacing:"3px",color:C.inkStrong,textTransform:"uppercase",marginBottom:14}}>{t.hero_tag}</div>
            <h1 style={{lineHeight:1.1,marginBottom:16,letterSpacing:-1.5}}>
              <em style={{color:C.ink}}>Handcrafts</em><br/>by Abu
            </h1>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:10,lineHeight:1.8}}>{t.hero_sub}</p>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:30,lineHeight:1.7}}>{t.hero_desc.split("\n").map((l,i)=><span key={i}>{l}{i===0&&<br/>}</span>)}</p>
            <div className="hero-btns" style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:28}}>
              <button className="btn btn-solid" style={{fontSize:"var(--text-body)",padding:"14px 30px"}} onClick={()=>goto("tienda")}>{t.hero_btn1}</button>
              <button className="btn btn-outline" style={{fontSize:"var(--text-body)",padding:"12px 26px"}} onClick={setCustomOpen}>{t.hero_btn2}</button>
            </div>
            <div className="hero-badges sans" style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:"var(--text-label)",color:C.ink}}>
              {t.hero_badges.map(b=><span key={b}>{b}</span>)}
            </div>
          </div>
        </div>

        <div style={{width:"100%",overflowX:"auto",paddingBottom:40,paddingLeft:"clamp(16px,5vw,72px)",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",msOverflowStyle:"none"}}>
          <div style={{display:"flex",gap:16,width:"max-content",paddingRight:"clamp(16px,5vw,72px)"}}>
            {allProducts.map((p,i) => {
              const isSoldHero = soldIds && soldIds.includes(p.id);
              const offsets = [0,-22,10,-14,6,-18,8];
              return (
                <div key={p.id} onClick={()=>goto("tienda")} style={{flexShrink:0,width:"clamp(172px,18vw,196px)",borderRadius:2,overflow:"hidden",border:`1px solid ${C.rule}`,cursor:"pointer",background:C.surface,transform:`translateY(${offsets[i%offsets.length]}px)`,transition:"border-color .2s",opacity:isSoldHero?0.7:1}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=C.ink;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=C.rule;}}>
                  <div className="product-media">
                    <img src={p.img} alt={p.name}/>
                  </div>
                  <div style={{padding:"14px 14px 16px",minHeight:84,display:"flex",flexDirection:"column",justifyContent:"space-between"}}>
                    <div style={{fontSize:"var(--text-label)",color:C.ink,lineHeight:1.35,marginBottom:6}}>{p.name}</div>
                    <div style={{fontSize:"var(--text-body)",color:isSoldHero?C.inkMuted:C.ink}}>{isSoldHero?t.tienda_sin_stock:"$"+p.price}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{padding:"var(--space-section) var(--gutter) 0",maxWidth:1100,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:16}}>
          {[
            { title:"Tejido en El Salvador", desc:"Cada pieza se teje una a la vez, con acabados pensados para regalo.", accent:C.ink },
            { title:"Pago seguro con Wompi", desc:"Pagás con tarjeta, Puntos Agrícola o Bitcoin desde la plataforma de Wompi.", accent:C.inkStrong },
            { title:"Encargos personalizados", desc:"Si no encontrás lo que buscás, pedí una versión a medida desde acá.", accent:C.ink },
          ].map((item) => (
            <div key={item.title} style={{background:C.surface,borderRadius:2,padding:"22px 20px",border:`1.5px solid ${C.rule}`}}>
              <div style={{width:42,height:4,borderRadius:2,background:item.accent,marginBottom:14}} />
              <h3 style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:8}}>{item.title}</h3>
              <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.7}}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={{padding:"var(--space-section) var(--gutter)",maxWidth:1100,margin:"0 auto"}}>
        <div style={{textAlign:"center",marginBottom:40}}>
          <h2 className="section-title serif">{t.sec_favoritos}</h2>
        </div>
        <div className="products-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:26}}>
          {allProducts.slice(0,3).map(p=><ProductCard key={p.id} p={p} addedId={addedId} onDetail={setDetailItem} soldIds={soldIds} onBuy={p2=>{ if(!soldIds.includes(p2.id)){addToCart(p2);setCheckoutOpen(true);} }}/>)}
        </div>
        <div style={{textAlign:"center",marginTop:36}}>
          <button className="btn btn-solid" style={{fontSize:"var(--text-body)",padding:"14px 36px"}} onClick={()=>goto("tienda")}>{t.sec_ver_todo}</button>
        </div>
      </section>

      {recentProducts.length > 0 && (
        <section style={{padding:"0 var(--gutter) var(--space-section)",maxWidth:1100,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"end",gap:16,marginBottom:24,flexWrap:"wrap"}}>
            <div>
              <h2 className="section-title serif" style={{marginBottom:6}}>Vistos recientemente</h2>
              <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>Las piezas que miraste hace poco.</p>
            </div>
          </div>
          <div className="products-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:24}}>
            {recentProducts.map((p)=><ProductCard key={p.id} p={p} addedId={addedId} onDetail={setDetailItem} soldIds={soldIds} onBuy={p2=>{ if(!soldIds.includes(p2.id)){addToCart(p2);setCheckoutOpen(true);} }}/>)}
          </div>
        </section>
      )}

      <section style={{background:C.surface,padding:"var(--space-section) var(--gutter)"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <h2 className="section-title serif">{t.sec_como}</h2>
          </div>
          <div className="steps-wrap" style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            {[
              {n:1,t:t.step1_t,d:t.step1_d},
              {n:2,t:t.step2_t,d:t.step2_d},
              {n:3,t:t.step3_t,d:t.step3_d},
              {n:4,t:t.step4_t,d:t.step4_d},
            ].map(s=>(
              <div key={s.n} className="step-card">
                <div className="step-num">{s.n}</div>
                <h4 style={{fontSize:"var(--text-body)",marginBottom:8,color:C.ink}}>{s.t}</h4>
                <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.65}}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{padding:"0 var(--gutter) var(--space-section)",maxWidth:1100,margin:"0 auto"}}>
        <div className="pay-strip" style={{background:C.surface,borderRadius:2,padding:"20px 32px",display:"flex",alignItems:"center",justifyContent:"center",gap:24,flexWrap:"wrap",border:`1.5px solid ${C.rule}`}}>
          <span className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{t.sec_pagos}</span>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <div className="on-ink" style={{display:"flex",alignItems:"center",gap:8,background:C.inkStrong,borderRadius:2,padding:"7px 14px"}}>
              <span style={{fontSize:"var(--text-body)",color:C.background,letterSpacing:.5}}>Wompi</span>
            </div>
            {["Visa","Mastercard","Puntos Agricola","Bitcoin / Chivo"].map(label => (
              <span key={label} style={{fontSize:"var(--text-label)",padding:"5px 10px",borderRadius:2,background:C.surface,color:C.ink,border:`1.5px solid ${C.rule}`}}>
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section style={{background:C.surface,padding:"var(--space-section) var(--gutter)"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <h2 className="section-title serif">{t.sec_testimonios}</h2>
          </div>
          <div className="testimonios-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:20}}>
            {t.testimonios.map((r,i)=>(
              <div key={i} style={{background:C.surface,borderRadius:2,padding:"24px",border:`1.5px solid ${C.rule}`}}>
                <div style={{display:"flex",gap:2,marginBottom:10}}>{"★★★★★".split("").map((s,j)=><span key={j} style={{color:C.ink,fontSize:"var(--text-body)"}}>{s}</span>)}</div>
                <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.75,marginBottom:16}}>{r.text}</p>
                <div style={{fontSize:"var(--text-body)",color:C.ink}}>— {r.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{padding:"var(--space-section) var(--gutter)",maxWidth:1100,margin:"0 auto",textAlign:"center"}}>
        <div className="on-ink" style={{background:C.inkStrong,borderRadius:2,padding:"56px 32px"}}>
          <div style={{fontSize:"var(--text-body)",marginBottom:16}}>Abu</div>
          <h2 style={{marginBottom:14}}>Handcrafts By Abu</h2>
          <p className="sans" style={{fontSize:"var(--text-body)",margin:"0 auto 28px",maxWidth:440,lineHeight:1.7}}>{t.hero_sub}</p>
          <button className="btn btn-white" style={{fontSize:"var(--text-body)",padding:"15px 36px"}} onClick={setCustomOpen}>{t.sec_encargo_cta}</button>
        </div>
      </section>

      <SiteFooter t={t}/>
    </div>
  );
}

function ShopPage({
 ProductCard,
  addedId,
  addToCart,
  allProducts,
  catFilter,
  lang,
  recentProducts,
  searchQ,
  setCatFilter,
  setCheckoutOpen,
  setCustomOpen,
  setDetailItem,
  setSearchQ,
  setSortBy,
  soldIds,
  sortBy,
  t
}) {
  const categories = [
    t.tienda_todos,
    ...new Set(allProducts.map(product => product.cat).filter(Boolean)),
  ];
  let filtered = allProducts
    .filter(p => catFilter===t.tienda_todos || catFilter==="Todos" || p.cat===catFilter)
    .filter(p => !searchQ || p.name.toLowerCase().includes(searchQ.toLowerCase()));
  if (sortBy==="precio-asc") filtered = [...filtered].sort((a,b)=>a.price-b.price);
  if (sortBy==="precio-desc") filtered = [...filtered].sort((a,b)=>b.price-a.price);
  if (sortBy==="nombre") filtered = [...filtered].sort((a,b)=>a.name.localeCompare(b.name));

  return (
    <div>
      <div style={{maxWidth:1100,margin:"0 auto",padding:"var(--space-section) var(--gutter)"}}>
      <div style={{marginBottom:32}}>
        <h1 className="serif section-title" style={{marginBottom:8}}>{t.tienda_title}</h1>
        <p className="sans" style={{color:C.ink,fontSize:"var(--text-body)"}}>{t.tienda_sub.replace("{n}",allProducts.length)}</p>
      </div>
      <div className="toolbar-wrap" style={{background:C.surface,borderRadius:2,padding:"16px 20px",display:"flex",flexWrap:"wrap",gap:12,alignItems:"center",marginBottom:28,border:`1.5px solid ${C.rule}`}}>
        <div style={{position:"relative",flexShrink:0,width:"100%",maxWidth:280}}>
          <input className="search-input" placeholder={t.tienda_buscar} value={searchQ} onChange={e=>setSearchQ(e.target.value)} style={{width:"100%"}}/>
        </div>
        <div className="pills-wrap" style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          {categories.map(c=><button key={c} className={`pill${catFilter===c||(!categories.includes(catFilter)&&c===t.tienda_todos)?" active":""}`} onClick={()=>setCatFilter(c)}>{c}</button>)}
        </div>
        <select className="sans" value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{marginLeft:"auto",padding:"9px 14px",borderRadius:2,border:`2px solid ${C.rule}`,background:C.surface,color:C.ink,fontSize:"var(--text-body)",cursor:"pointer",outline:"none"}}>
          <option value="default">{lang==="es"?"Ordenar":"Sort"}</option>
          <option value="precio-asc">{lang==="es"?"Precio ↑":"Price ↑"}</option>
          <option value="precio-desc">{lang==="es"?"Precio ↓":"Price ↓"}</option>
          <option value="nombre">{lang==="es"?"Nombre A-Z":"Name A-Z"}</option>
        </select>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:22}}>
          <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>
            {filtered.length} {lang==="es" ? "productos visibles" : "products visible"}
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[
              lang==="es" ? "Pieza única" : "One of a kind",
              lang==="es" ? "Pago seguro" : "Secure checkout",
              lang==="es" ? "Envíos a SV y exterior" : "SV and international shipping",
            ].map((label) => (
              <span key={label} className="sans" style={{fontSize:"var(--text-label)",padding:"6px 10px",borderRadius:2,background:C.surface,color:C.ink}}>
                {label}
              </span>
            ))}
          </div>
        </div>
        {filtered.length===0?(
          <div style={{textAlign:"center",padding:"60px 0",color:C.ink}}>
            <div style={{fontSize:"var(--text-body)",marginBottom:12}}>No items</div>
            <p className="sans" style={{fontSize:"var(--text-body)"}}>{lang==="es"?"No se encontraron productos":"No products found"}</p>
          </div>
        ):(
          <div className="products-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(268px,1fr))",gap:26}}>
            {filtered.map(p=><ProductCard key={p.id} p={p} addedId={addedId} onDetail={setDetailItem} soldIds={soldIds} onBuy={p2=>{ if(!soldIds.includes(p2.id)){addToCart(p2);setCheckoutOpen(true);} }}/>)}
          </div>
        )}
        {recentProducts.length > 0 && (
          <div style={{marginTop:28}}>
            <h3 style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:8}}>You may also like</h3>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:18}}>Según lo que miraste hace poco.</p>
            <div className="products-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:20}}>
              {recentProducts.map((p)=><ProductCard key={`recent-${p.id}`} p={p} addedId={addedId} onDetail={setDetailItem} soldIds={soldIds} onBuy={p2=>{ if(!soldIds.includes(p2.id)){addToCart(p2);setCheckoutOpen(true);} }}/>)}
            </div>
          </div>
        )}
        <div style={{marginTop:28,background:C.surface,borderRadius:2,padding:"22px 24px",border:`1.5px solid ${C.rule}`,display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
          {[
            { title:lang==="es"?"Comprá con confianza":"Shop with confidence", desc:lang==="es"?"Una pieza se marca agotada solo después de que Wompi confirma el pago." :"Items only switch to sold after payment confirmation." },
              { title:lang==="es"?"Entrega clara":"Delivery made clear", desc:lang==="es"?"Mirá la página de Entrega para ver tiempos y costos por zona." :"Check the Delivery page for times and costs by zone." },
            { title:lang==="es"?"¿No ves tu idea?":"Need something custom?", desc:lang==="es"?"Abu recibe encargos a medida. Mandale referencias y te dice si puede tejerlo." :"Abu takes custom orders. Send references and she will tell you if she can make it." },
          ].map((item) => (
            <div key={item.title}>
              <h3 style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:8}}>{item.title}</h3>
              <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.7}}>{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="bottom-cta" style={{marginTop:64,background:C.surface,borderRadius:2,padding:"36px 40px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:20,border:`1.5px solid ${C.rule}`}}>
          <div>
            <h3 style={{fontSize:"var(--text-body)",marginBottom:6}}>{t.tienda_encargo_cta}</h3>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{t.tienda_encargo_sub}</p>
        </div>
        <button className="btn btn-solid" style={{fontSize:"var(--text-body)",padding:"14px 28px"}} onClick={setCustomOpen}>{t.tienda_encargo_btn}</button>
      </div>
      </div>
      <SiteFooter t={t}/>
    </div>
  );
}

function AboutPage({ goto, openFaq, setCustomOpen, setOpenFaq, t }) {
  return (
    <div>
      <section className="nosotros-hero" style={{background:C.surface,padding:"var(--space-section) var(--gutter)",textAlign:"center"}}>
        <h1 className="serif" style={{marginBottom:16}}>{t.nos_title}</h1>
        <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,maxWidth:600,margin:"0 auto",lineHeight:1.8}}>{t.nos_sub}</p>
      </section>
      <section style={{padding:"var(--space-section) var(--gutter)",maxWidth:1100,margin:"0 auto"}}>
        <div className="about-grid" style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:44,alignItems:"center",background:C.surface,borderRadius:2,padding:"44px 48px",border:`1.5px solid ${C.rule}`}}>
          <div style={{width:140,height:140,borderRadius:"50%",background:C.surface,border:`4px solid ${C.ink}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"var(--text-body)",flexShrink:0}}>Abu</div>
          <div>
            <h2 style={{fontSize:"var(--text-body)",marginBottom:14,color:C.ink}}>{t.nos_hola}</h2>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.85,marginBottom:12}}>{t.nos_bio1}</p>
            <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.85,marginBottom:20}}>{t.nos_bio2}</p>
            <a href={`https://www.instagram.com/${IG_HANDLE}/`} target="_blank" rel="noreferrer" className="btn btn-purple sans" style={{textDecoration:"none",display:"inline-flex",padding:"11px 24px",fontSize:"var(--text-body)"}}>{t.nos_ig}</a>
          </div>
        </div>
      </section>
      <section style={{background:C.surface,padding:"var(--space-section) var(--gutter)"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <h2 className="section-title serif" style={{textAlign:"center",marginBottom:10}}>{t.nos_valores}</h2>
          <div className="values-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:20,marginTop:28}}>
            {t.valores.map((v,i)=>(
              <div key={i} className="value-card">
                <h4 style={{fontSize:"var(--text-body)",marginBottom:8,color:C.ink}}>{v.t}</h4>
                <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.65}}>{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{padding:"var(--space-section) var(--gutter)",maxWidth:1100,margin:"0 auto"}}>
        <h2 className="section-title serif" style={{textAlign:"center",marginBottom:10}}>{t.nos_proceso}</h2>
        <div className="steps-wrap" style={{display:"flex",gap:16,flexWrap:"wrap",marginTop:28}}>
          {t.proceso.map(s=>(
            <div key={s.n} className="step-card">
              <div className="step-num">{s.n}</div>
              <h4 style={{fontSize:"var(--text-body)",marginBottom:8,color:C.ink}}>{s.t}</h4>
              <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.65}}>{s.d}</p>
            </div>
          ))}
        </div>
      </section>
      <section style={{padding:"0 var(--gutter) var(--space-section)",maxWidth:1100,margin:"0 auto"}}>
        <div className="on-ink" style={{background:C.inkStrong,borderRadius:2,padding:"48px",textAlign:"center"}}>
          <h2 style={{marginBottom:14}}>{t.nos_cta_title}</h2>
          <p className="sans" style={{fontSize:"var(--text-body)",marginBottom:24}}>{t.nos_cta_sub}</p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="btn btn-white" style={{fontSize:"var(--text-body)",padding:"13px 28px"}} onClick={()=>goto("tienda")}>{t.nos_cta_btn1}</button>
            <button className="btn btn-white" style={{fontSize:"var(--text-body)",padding:"13px 28px"}} onClick={setCustomOpen}>{t.nos_cta_btn2}</button>
          </div>
        </div>
      </section>
      <section style={{padding:"0 var(--gutter) var(--space-section)",maxWidth:1100,margin:"0 auto"}}>
        <h2 className="section-title serif" style={{marginBottom:28,textAlign:"center"}}>{t.nos_faq_title}</h2>
        {t.faqs.map(([q,a],i)=>(
          <div key={i} className="faq-item">
            <button className="faq-q" onClick={()=>setOpenFaq(openFaq===i?null:i)}>
              {q} <span style={{fontSize:"var(--text-body)",color:C.ink,transition:"transform .2s",transform:openFaq===i?"rotate(45deg)":"rotate(0deg)"}}>+</span>
            </button>
            <div className={`faq-a${openFaq===i?" open":""}`}>{a}</div>
          </div>
        ))}
      </section>
      <SiteFooter t={t}/>
    </div>
  );
}

function DeliveryPage({ t }) {
  const [location, setLocation] = useState("");
  const zoneId = detectDeliveryZone(location);
  const matchedZone = SHIPPING_ZONES.find((zone) => zone.id === zoneId) || null;
  const copy = {
    title: t.delivery_title,
    sub: t.delivery_sub,
    lookupLabel: t.delivery_lookup_label,
    lookupPlaceholder: t.delivery_lookup_placeholder,
    lookupButton: t.delivery_lookup_button,
    lookupEmpty: t.delivery_lookup_empty,
    lookupResult: t.delivery_lookup_result,
    lookupFallback: t.delivery_lookup_fallback,
    howTitle: t.delivery_how_title,
    howSteps: t.delivery_how_steps,
    pickupNote: t.delivery_pickup_note,
  };

  return (
    <div>
      <div style={{maxWidth:1100,margin:"0 auto",padding:"var(--space-section) var(--gutter)"}}>
      <div style={{marginBottom:32}}>
        <h1 className="serif section-title" style={{marginBottom:8}}>{copy.title}</h1>
        <p className="sans" style={{color:C.ink,fontSize:"var(--text-body)",lineHeight:1.7}}>{copy.sub}</p>
      </div>

      <div style={{background:C.surface,borderRadius:2,padding:"24px",border:`1.5px solid ${C.rule}`,marginBottom:28}}>
        <label className="sans" style={{display:"block",fontSize:"var(--text-body)",color:C.ink,marginBottom:8}}>{copy.lookupLabel}</label>
        <input
          value={location}
          onChange={(event) => setLocation(event.target.value)}
          placeholder={copy.lookupPlaceholder}
          style={{width:"100%",padding:"13px 16px",borderRadius:2,border:`2px solid ${C.rule}`,fontSize:"var(--text-body)",outline:"none",boxSizing:"border-box"}}
        />
        <div style={{marginTop:14,background:matchedZone ? C.surface : C.surface,borderRadius:2,padding:"16px 18px"}}>
          <div className="sans" style={{fontSize:"var(--text-label)",color:C.ink,marginBottom:6}}>
            {matchedZone ? copy.lookupResult : copy.lookupButton}
          </div>
          {location.trim() === "" && (
            <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{copy.lookupEmpty}</div>
          )}
          {location.trim() !== "" && matchedZone && (
            <div>
              <div style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:4}}>{matchedZone.label}</div>
              <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>Entrega estimada: {matchedZone.days}</div>
              <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>
                Costo base: {matchedZone.displayPrice || (matchedZone.price === 0 ? "Gratis" : `$${matchedZone.price}`)}
              </div>
            </div>
          )}
          {location.trim() !== "" && !matchedZone && (
            <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{copy.lookupFallback}</div>
          )}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:18,marginBottom:36}}>
        {SHIPPING_ZONES.map((zone) => (
          <div key={zone.id} style={{background:C.surface,borderRadius:2,padding:"20px",border:`1.5px solid ${matchedZone?.id===zone.id ? C.ink : C.surface}`}}>
            <div style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:6}}>{zone.label}</div>
            <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginBottom:10}}>{zone.days}</div>
            <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink}}>{zone.displayPrice || (zone.price === 0 ? "Gratis" : `$${zone.price}`)}</div>
          </div>
        ))}
      </div>

      <div style={{background:C.surface,borderRadius:2,padding:"30px 28px",border:`1.5px solid ${C.rule}`}}>
        <h2 className="serif" style={{fontSize:"var(--text-body)",marginBottom:16}}>{copy.howTitle}</h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
          {copy.howSteps.map((step, index) => (
            <div key={index} style={{background:C.surface,borderRadius:2,padding:"18px 18px",border:`1.5px solid ${C.rule}`}}>
              <div style={{fontSize:"var(--text-label)",color:C.ink,marginBottom:8}}>PASO {index + 1}</div>
              <div className="sans" style={{fontSize:"var(--text-body)",color:C.ink,lineHeight:1.7}}>{step}</div>
            </div>
          ))}
        </div>
        <p className="sans" style={{fontSize:"var(--text-body)",color:C.ink,marginTop:16}}>{copy.pickupNote}</p>
      </div>
      </div>
      <SiteFooter t={t}/>
    </div>
  );
}

export { AboutPage, DeliveryPage, HomePage, NavBar, PaymentResultPage, ShopPage };
